import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "../../components/AppToast";
import Select from "../../components/SelectInput";
import { createAppointment } from "../../services/appointment/appointment";
import { fetchASN } from "../../services/asnAPI/asn";
import { fetchSalesOrder } from "../../services/salesOrder/salesOrder";
import { fetchDoorlocs } from "../../services/doorloc/doorloc";
import { getallpartner } from "../../services/partner/partner";
import {
    APPOINTMENT_TYPE_OPTIONS,
    TRAILER_TYPE_OPTIONS,
    appointmentTypeLabelToApiValue,
} from "./formFields/appointmentFields";
import { formatToDisplayDate } from "../../utils/dateFormatter";

/** Partner list API → single-select options (value = partner_id). */
function buildCarrierPartnerOptions(apiRows = []) {
    const opts = [];
    const seen = new Set();
    for (const rec of apiRows) {
        const id = String(rec?.partner_id ?? "").trim();
        if (!id || seen.has(id)) continue;
        seen.add(id);
        const name = String(rec?.partner_name ?? "").trim();
        opts.push({
            value: id,
            label: name ? `${name} (${id})` : id,
        });
    }
    return opts.sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );
}

/**
 * Same response shape as door locations list (`DoorLocList`): array or nested `data`.
 */
function normalizeDoorlocListResponse(res) {
    if (Array.isArray(res)) return res;
    const inner = res?.data?.data ?? res?.data;
    return Array.isArray(inner) ? inner : [];
}

/** Door select: one option per distinct `Loc` from door-location API (value = label = Loc). */
function buildDoorLocationOptions(list = []) {
    const seen = new Set();
    const opts = [];
    for (const item of list) {
        const loc = String(item?.Loc ?? "").trim();
        if (!loc || seen.has(loc)) continue;
        seen.add(loc);
        opts.push({ value: loc, label: loc });
    }
    return opts.sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );
}

/** Random 10-digit numeric string (1_000_000_000–9_999_999_999) for `AppointmentKey`. */
function generateAppointmentKey() {
    const min = 1_000_000_000;
    const max = 9_999_999_999;
    return String(Math.floor(Math.random() * (max - min + 1)) + min);
}

/** datetime-local value → ISO string for API */
function datetimeLocalToIso(local) {
    if (!local || !String(local).trim()) return "";
    const d = new Date(local);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

function displayCell(v) {
    if (v == null || v === "") return "—";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
}

function formatExpectedDate(raw) {
    if (raw == null || raw === "") return "—";
    const formatted = formatToDisplayDate(raw, true, "24h");
    return formatted || displayCell(raw);
}

/** Build lookup key → full API row for ASN / sales order lists */
function buildSourceRecordsMapInbound(rows) {
    const map = {};
    for (const rec of rows) {
        const h = rec.asnDetails || {};
        const key = String(h.asnNumber ?? "").trim();
        if (key) map[key] = rec;
    }
    return map;
}

function buildSourceRecordsMapOutbound(list) {
    const map = {};
    for (const item of list) {
        const so = item.soDetails || {};
        const key = String(so.soNumber ?? item.soNumber ?? "").trim();
        if (key) map[key] = item;
    }
    return map;
}

const initialForm = () => ({
    AppointmentType: "",
    Status: "New",
    Door: "",
    Carrier: "",
    Trailer: "",
    TrailerType: "",
    GMTStartDateAndTime: "",
    GMTEndDateAndTime: "",
    Owner: "",
    CustomerVendor: "",
});

export default function CreateAppointment({
    onCancel,
    onCreated,
    /** From ASN list: `{ AppointmentType: "Inbound", sourceKeys: string[] }` (ASN numbers). */
    initialPreset,
}) {
    const [form, setForm] = useState(() => {
        const base = initialForm();
        if (initialPreset?.AppointmentType) {
            base.AppointmentType = initialPreset.AppointmentType;
        }
        return base;
    });
    const [loading, setLoading] = useState(false);
    const [sourceOptions, setSourceOptions] = useState([]);
    const [sourceLoading, setSourceLoading] = useState(false);
    const [selectedSourceKeys, setSelectedSourceKeys] = useState([]);
    const [doorLocOptions, setDoorLocOptions] = useState([]);
    const [doorLocLoading, setDoorLocLoading] = useState(false);
    const [carrierOptions, setCarrierOptions] = useState([]);
    const [carrierLoading, setCarrierLoading] = useState(false);
    /** Full API row per source key (ASN # or SO #) for detail table */
    const [sourceRecordsByKey, setSourceRecordsByKey] = useState({});

    const inboundPresetAppliedRef = useRef(false);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setSelectedSourceKeys([]);
            setSourceRecordsByKey({});
            if (
                form.AppointmentType !== "Inbound" &&
                form.AppointmentType !== "Outbound"
            ) {
                setSourceOptions([]);
                setSourceLoading(false);
                return;
            }
            setSourceLoading(true);
            try {
                if (form.AppointmentType === "Inbound") {
                    const res = await fetchASN(0, 200, []);
                    const rows = Array.isArray(res?.data) ? res.data : [];
                    if (!cancelled) {
                        setSourceRecordsByKey(buildSourceRecordsMapInbound(rows));
                    }
                    const opts = rows
                        .map((rec, idx) => {
                            const h = rec.asnDetails || {};
                            const key = String(h.asnNumber ?? "").trim();
                            if (!key) return null;
                            return {
                                value: key,
                                label: key + (h.owner ? ` — ${h.owner}` : ""),
                                id: `${key}-${idx}`,
                            };
                        })
                        .filter(Boolean);
                    if (!cancelled) setSourceOptions(opts);
                } else {
                    const res = await fetchSalesOrder([], 0, 200);
                    const list = Array.isArray(res?.data) ? res.data : [];
                    if (!cancelled) {
                        setSourceRecordsByKey(buildSourceRecordsMapOutbound(list));
                    }
                    const opts = list
                        .map((item, idx) => {
                            const so = item.soDetails || {};
                            const key = String(
                                so.soNumber ?? item.soNumber ?? ""
                            ).trim();
                            if (!key) return null;
                            return {
                                value: key,
                                label:
                                    key +
                                    (so.owner || item.owner
                                        ? ` — ${so.owner ?? item.owner}`
                                        : ""),
                                id: `${key}-${idx}`,
                            };
                        })
                        .filter(Boolean);
                    if (!cancelled) setSourceOptions(opts);
                }
            } catch (err) {
                const msg =
                    err?.message ||
                    (form.AppointmentType === "Inbound"
                        ? "Failed to load ASN list"
                        : "Failed to load sales orders");
                toast.error(typeof msg === "string" ? msg : "Failed to load sources");
                if (!cancelled) {
                    setSourceOptions([]);
                    setSourceRecordsByKey({});
                }
            } finally {
                if (!cancelled) setSourceLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [form.AppointmentType]);

    /** After Inbound ASN list loads, pre-select ASNs from ASN table (Inbound = receipt / ASN). */
    useEffect(() => {
        if (!initialPreset?.sourceKeys?.length) return;
        if (initialPreset.AppointmentType !== "Inbound") return;
        if (inboundPresetAppliedRef.current) return;
        if (form.AppointmentType !== "Inbound") return;
        if (sourceLoading) return;
        inboundPresetAppliedRef.current = true;
        setSelectedSourceKeys([...new Set(initialPreset.sourceKeys.map((k) => String(k).trim()).filter(Boolean))]);
    }, [initialPreset, form.AppointmentType, sourceLoading]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setDoorLocLoading(true);
            try {
                const res = await fetchDoorlocs(0, 500);
                const list = normalizeDoorlocListResponse(res);
                const opts = buildDoorLocationOptions(list);
                if (!cancelled) setDoorLocOptions(opts);
            } catch (err) {
                const msg =
                    err?.message || "Failed to load door locations";
                toast.error(typeof msg === "string" ? msg : "Failed to load door locations");
                if (!cancelled) setDoorLocOptions([]);
            } finally {
                if (!cancelled) setDoorLocLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setCarrierLoading(true);
            try {
                const res = await getallpartner(0, 500);
                const rows = Array.isArray(res?.data) ? res.data : [];
                const opts = buildCarrierPartnerOptions(rows);
                if (!cancelled) setCarrierOptions(opts);
            } catch (err) {
                const msg = err?.message || "Failed to load partners";
                toast.error(typeof msg === "string" ? msg : "Failed to load partners");
                if (!cancelled) setCarrierOptions([]);
            } finally {
                if (!cancelled) setCarrierLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const removeSourceKey = useCallback((key) => {
        setSelectedSourceKeys((prev) => prev.filter((k) => k !== key));
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.AppointmentType) {
            toast.error("Appointment Type is required");
            return;
        }
        if (!selectedSourceKeys.length) {
            toast.error(
                form.AppointmentType === "Inbound"
                    ? "Select at least one ASN"
                    : "Select at least one sales order"
            );
            return;
        }

        setLoading(true);
        try {
            const appointmentKey = generateAppointmentKey();
            const typeValue = appointmentTypeLabelToApiValue(form.AppointmentType);
            const header = {
                AppointmentKey: appointmentKey,
                appointmenttype: typeValue,
                Status: form.Status || "New",
                Door: form.Door || "",
                Carrier: form.Carrier || "",
                Trailer: form.Trailer || "",
                TrailerType: form.TrailerType || "",
                Owner: form.Owner || "",
                CustomerVendor: form.CustomerVendor || "",
            };
            const startIso = datetimeLocalToIso(form.GMTStartDateAndTime);
            const endIso = datetimeLocalToIso(form.GMTEndDateAndTime);
            if (startIso) header.GMTStartDateAndTime = startIso;
            if (endIso) header.GMTEndDateAndTime = endIso;

            const appointmentdetails = selectedSourceKeys.map((sourcekey) => ({
                sourcekey,
                sourcetype: typeValue,
                insert: true,
            }));

            await createAppointment({
                AppointmentHeader: header,
                appointmentdetails,
            });
            toast.success(
                `Appointment created successfully. Key: ${appointmentKey}`
            );
            setForm(initialForm());
            setSelectedSourceKeys([]);
            setSourceRecordsByKey({});
            onCreated?.();
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                "Failed to create appointment";
            toast.error(typeof msg === "string" ? msg : "Failed to create appointment");
        } finally {
            setLoading(false);
        }
    };

    const showAppointmentDetails =
        (form.AppointmentType === "Inbound" ||
            form.AppointmentType === "Outbound") &&
        selectedSourceKeys.length > 0;

    return (
        <form onSubmit={handleSubmit}>
            <div className="mb-3 border-bottom pb-2">
                <span className="text-primary fw-semibold">New appointment</span>
                <p className="text-muted small mb-0 mt-1">
                    Appointment key is generated automatically (10 digits) when you
                    create.
                </p>
            </div>

            <div className="row gx-2 align-items-start">
                <Select
                    columnClass="col-md-2"
                    className="mb-3"
                    label="Appointment type"
                    name="AppointmentType"
                    value={form.AppointmentType}
                    options={APPOINTMENT_TYPE_OPTIONS}
                    onChange={handleChange}
                    isRequired
                />
                <Select
                    columnClass="col-md-2"
                    className="mb-3"
                    label="Source"
                    name="AppointmentSources"
                    value={selectedSourceKeys}
                    options={sourceOptions.map(({ value, label }) => ({
                        value,
                        label,
                    }))}
                    onChange={(e) => setSelectedSourceKeys(e.target.value)}
                    multiple
                    size={1}
                    disabled={
                        !form.AppointmentType ||
                        form.AppointmentType === "" ||
                        sourceLoading
                    }
                    isRequired
                >
                    <div className="form-text">
                        {sourceLoading
                            ? "Loading…"
                            : form.AppointmentType === "Inbound"
                              ? "ASN(s). "
                              : form.AppointmentType === "Outbound"
                                ? "Sales order(s). "
                                : "Choose appointment type. "}
                    </div>
                    {!sourceLoading &&
                        form.AppointmentType === "Inbound" &&
                        sourceOptions.length === 0 && (
                            <p className="text-warning small mb-0 mt-1">
                                No ASNs returned.
                            </p>
                        )}
                    {!sourceLoading &&
                        form.AppointmentType === "Outbound" &&
                        sourceOptions.length === 0 && (
                            <p className="text-warning small mb-0 mt-1">
                                No sales orders returned.
                            </p>
                        )}
                </Select>
                <Select
                    columnClass="col-md-2"
                    className="mb-3"
                    label="Door"
                    name="Door"
                    value={form.Door}
                    options={doorLocOptions}
                    onChange={handleChange}
                    disabled={doorLocLoading}
                />
                <Select
                    columnClass="col-md-2"
                    className="mb-3"
                    label="Carrier"
                    name="Carrier"
                    value={form.Carrier}
                    options={carrierOptions}
                    onChange={handleChange}
                    disabled={carrierLoading}
                >
                    <div className="form-text">
                        {carrierLoading
                            ? "Loading partners…"
                            : "Partner ID from partner management."}
                    </div>
                </Select>
                <div className="col-md-2">
                    <div className="form-group mb-3">
                        <label className="form-label" htmlFor="Trailer">
                            Trailer
                        </label>
                        <input
                            id="Trailer"
                            name="Trailer"
                            className="form-control"
                            value={form.Trailer}
                            onChange={handleChange}
                            autoComplete="off"
                        />
                    </div>
                </div>
                <Select
                    columnClass="col-md-2"
                    className="mb-3"
                    label="Trailer type"
                    name="TrailerType"
                    value={form.TrailerType}
                    options={TRAILER_TYPE_OPTIONS}
                    onChange={handleChange}
                />
                <div className="col-md-2">
                    <div className="form-group mb-3">
                        <label className="form-label" htmlFor="Owner">
                            Owner
                        </label>
                        <input
                            id="Owner"
                            name="Owner"
                            className="form-control"
                            value={form.Owner}
                            onChange={handleChange}
                            autoComplete="off"
                        />
                    </div>
                </div>
                <div className="col-md-2">
                    <div className="form-group mb-3">
                        <label className="form-label" htmlFor="CustomerVendor">
                            Customer / vendor
                        </label>
                        <input
                            id="CustomerVendor"
                            name="CustomerVendor"
                            className="form-control"
                            value={form.CustomerVendor}
                            onChange={handleChange}
                            autoComplete="off"
                        />
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="form-group mb-3">
                        <label className="form-label" htmlFor="GMTStartDateAndTime">
                            Start Date & Time
                        </label>
                        <input
                            id="GMTStartDateAndTime"
                            name="GMTStartDateAndTime"
                            type="datetime-local"
                            className="form-control"
                            value={form.GMTStartDateAndTime}
                            onChange={handleChange}
                        />
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="form-group mb-3">
                        <label className="form-label" htmlFor="GMTEndDateAndTime">
                            End Date & Time
                        </label>
                        <input
                            id="GMTEndDateAndTime"
                            name="GMTEndDateAndTime"
                            type="datetime-local"
                            className="form-control"
                            value={form.GMTEndDateAndTime}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            {showAppointmentDetails && (
                <div className="mt-4 pt-3 border-top">
                    <h6 className="text-primary fw-semibold mb-2">
                        Appointment details
                    </h6>
                    <p className="text-muted small mb-2">
                        Selected{" "}
                        {form.AppointmentType === "Inbound"
                            ? "ASN"
                            : "sales order"}{" "}
                        lines. Remove drops a line (also clears it in Source above).
                    </p>
                    <div className="table-responsive">
                        <table className="table table-sm table-bordered align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: 44 }}>#</th>
                                    <th style={{ minWidth: 120 }}>
                                        {form.AppointmentType === "Inbound"
                                            ? "ASN"
                                            : "Sales order"}
                                    </th>
                                    <th>Owner</th>
                                    <th style={{ minWidth: 88 }}>Status</th>
                                    <th style={{ minWidth: 130 }}>
                                        Expected arrival
                                    </th>
                                    <th style={{ minWidth: 120 }}>
                                        {form.AppointmentType === "Inbound"
                                            ? "External ASN #"
                                            : "External order #"}
                                    </th>
                                    <th style={{ minWidth: 110 }}>
                                        {form.AppointmentType === "Inbound"
                                            ? "External ref."
                                            : "Order date"}
                                    </th>
                                    <th>Carrier</th>
                                    <th
                                        style={{ width: 96 }}
                                        className="text-center"
                                    >
                                        Remove
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedSourceKeys.map((key, idx) => {
                                    const rec = sourceRecordsByKey[key];
                                    const inbound = form.AppointmentType === "Inbound";
                                    const h = rec?.asnDetails || {};
                                    const s = rec?.soDetails || {};
                                    const load = rec?.load || {};
                                    const carr = rec?.carrier || {};
                                    const expectedArrivalRaw = inbound
                                        ? load.expectedArrivalDate
                                        : s.expectedReceiptDate ??
                                          s.expectedArrivalDate;
                                    const externalRef = inbound
                                        ? h.externalAsnNumber ??
                                          h.externalASNReciept ??
                                          ""
                                        : s.externalSoNumber ??
                                          s.externalOrder1 ??
                                          "";
                                    const carrierName =
                                        carr.carrierName ??
                                        carr.name ??
                                        carr.carrier ??
                                        "";
                                    const owner = inbound
                                        ? h.owner
                                        : s.owner ?? rec?.owner;
                                    const status = inbound
                                        ? h.receiptStatus
                                        : s.status;
                                    const col4 = inbound
                                        ? h.externalAsnNumber ??
                                          h.externalASNReciept ??
                                          ""
                                        : s.orderDate ?? "";
                                    return (
                                        <tr key={key}>
                                            <td>{idx + 1}</td>
                                            <td className="fw-medium">{key}</td>
                                            <td>{displayCell(owner)}</td>
                                            <td>{displayCell(status)}</td>
                                            <td>
                                                {formatExpectedDate(
                                                    expectedArrivalRaw
                                                )}
                                            </td>
                                            <td>{displayCell(externalRef)}</td>
                                            <td>{displayCell(col4)}</td>
                                            <td>{displayCell(carrierName)}</td>
                                            <td className="text-center text-nowrap">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-danger btn-sm"
                                                    title="Remove from appointment"
                                                    onClick={() =>
                                                        removeSourceKey(key)
                                                    }
                                                >
                                                    <i
                                                        className="bi bi-trash"
                                                        aria-hidden="true"
                                                    />{" "}
                                                    Remove
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="d-flex justify-content-center gap-2 mt-3">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onCancel}
                    disabled={loading}
                >
                    Back
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? "Saving…" : "Create appointment"}
                </button>
            </div>
        </form>
    );
}
